// src/components/pages/SenatePage.js - With theme support
import React, { useState, useEffect } from 'react';
import './SenatePage.css';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSenateConfig } from '../../utils/feedsConfig';
import MobileFeedCarousel from '../feed/MobileFeedCarousel';
import FeedSection from '../feed/FeedSection';
import SearchNavBar from '../search/SearchNavBar';
import MobileNavigation from '../navigation/MobileNavigation';
import NotificationButton from '../notifications/NotificationButton';
import openCapacitorSite from '../../utils/browserUtils';
import { MEMBER_TYPES } from '../../utils/membersConfig';
import MembersCarousel from '../members/MembersCarousel';
import DeskCarousel from '../carousel/DeskCarousel';
import MembersCarouselSection from '../members/MembersCarouselSection';

const SenatePage = ({ carouselInterval = 5000 }) => {
  const { t } = useTranslation('common');
  const [isMobile, setIsMobile] = useState(false);
  const senateConfig = getSenateConfig();
  const { departmentInfo, committees } = senateConfig;

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSenateNotificationChange = (isSubscribed, feedId, feedName, branch, topic) => {
    console.log(`Senate notification ${isSubscribed ? 'enabled' : 'disabled'}:`, {
      feedId, feedName, branch, topic
    });
  };

  /**
   * Generate feed variations with different skip values for carousel display
   */
  const generateFeedVariations = (baseFeed, count = 10) => {
    const variations = [];
    for (let i = 0; i < count; i++) {
      let endpoint = baseFeed.apiEndpoint || baseFeed.apiParams;
      
      if (endpoint.includes('skip=')) {
        endpoint = endpoint.replace(/skip=\d+/, `skip=${i}`);
      } else {
        endpoint += `&skip=${i}`;
      }
      
      variations.push({
        ...baseFeed,
        id: `${baseFeed.id}-${i}`,
        apiEndpoint: endpoint,
        title: baseFeed.title || baseFeed.name
      });
    }
    return variations;
  };

  /**
   * Transform committee feed to match DeskCarousel expected format
   * Senate uses newsFeed instead of meetingFeed
   */
  const transformCommitteeForCarousel = (committee) => ({
    type: 'normal',
    id: committee.id,
    title: committee.shortName || committee.name,
    apiEndpoint: committee.newsFeed?.apiEndpoint,
    feedName: committee.name,
    branch: 'Legislative',
    topic: 'N/A',
    buttonName: committee.newsFeed?.buttonName || 'Document',
    viewAllLink: `/us/senate/${committee.id}`
  });

  // Mobile Layout
  if (isMobile) {
    // Convert committees to the format expected by MobileFeedCarousel
    // Senate uses newsFeed instead of meetingFeed - filter out committees without newsFeed
    const committeeMobileFeeds = committees
      .filter(committee => committee.newsFeed?.apiEndpoint)
      .map(committee => ({
        id: committee.id,
        name: committee.shortName,
        displayType: 'normal',
        apiParams: committee.newsFeed.apiEndpoint,
        feedBranch: 'Legislative',
        feedTopic: 'N/A',
        buttonName: committee.newsFeed.buttonName,
        agencyRoute: `/us/senate/${committee.id}`,
        departmentPage: true
      }));

    return (
      <div id='homebg' className="senate-page-root">
        {/* Match HomePage spacing - 70px navbar clearance */}
        <div style={{ marginTop: '45px', paddingTop: '5px' }}>
          
          {/* Section Header */}
          <div className="mobile-section-header" style={{ padding: '10px 15px', marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="senate-profile-image-mobile" style={{ 
                width: '70px', 
                height: '70px', 
                borderRadius: '50%', 
                overflow: 'hidden',
                background: 'var(--bg-card)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src={departmentInfo.image} 
                  alt={departmentInfo.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              </div>
              
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '17px' }}>
                    {departmentInfo.name}
                  </h3>
                  {/* <NotificationButton
                    feedId="senate-branch-us"
                    feedName="Senate Updates"
                    branch="legislative"
                    topic="legislative"
                    variant="outline-light"
                    size="sm"
                    showLabel={false}
                    onNotificationStateChange={handleSenateNotificationChange}
                  /> */}
                </div>
                
                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '6px' }}>
                  {departmentInfo.description}
                  
                  <div style={{ marginTop: '6px' }}>
                    <a 
                      href={departmentInfo.officialURL}
                      onClick={(e) => {
                        e.preventDefault();
                        openCapacitorSite(departmentInfo.officialURL);
                      }}
                      style={{ color: 'var(--accent-hover)', fontSize: '12px' }}
                    >
                      {departmentInfo.officialURL}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Use MobileFeedCarousel - horizontal sliding cards */}
          <div className="mobile-feeds-container">
            <MembersCarousel 
              memberType={MEMBER_TYPES.SENATE}
              limit={10}
              title={t('common:usSenators', 'U.S. Senators')}
              viewAllLink="/us/senators"
              viewAllText={t('common:viewAllSenators', 'View All Senators')}
            />
            <MobileFeedCarousel feeds={committeeMobileFeeds} />
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout - matching HousePage pattern
  // Group committees into pairs for rows (only those with newsFeed)
  const validCommittees = committees.filter(committee => committee.newsFeed?.apiEndpoint);
  const committeeRows = [];
  for (let i = 0; i < validCommittees.length; i += 2) {
    committeeRows.push(validCommittees.slice(i, i + 2));
  }

  return (
    <div id='homebg' className="senate-page-root">
      {/* Desktop Senate Header */}
      <div style={{ position: 'relative', padding: '30px 0' }}>
        <div id='profileImage' style={{ 
          width: '130px', 
          height: '130px', 
          borderRadius: '100%', 
          overflow: 'hidden', 
          marginLeft: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-card-solid)'
        }}>
          <img 
            src={departmentInfo.image} 
            alt={departmentInfo.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>

        <div style={{ 
          position: 'absolute', 
          top: '0', 
          left: '250px', 
          display: 'flex', 
          flexDirection: 'column',
          width: 'calc(100% - 250px)'
        }}>
          <div style={{ color: 'var(--text-primary)', fontSize: '20px' }}> 
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>{departmentInfo.name}</h3>
            </div>
            
            <div style={{ color: 'var(--text-secondary)', fontSize: '16px', width: '80%' }}>
              <span>{departmentInfo.description}</span>
              <br />
              <div style={{ color: 'var(--text-primary)', fontSize: '18px', marginTop: '10px' }}>
                <span>
                  <Link 
                    style={{ color: 'var(--accent-hover)' }} 
                    onClick={(e) => {
                      e.preventDefault();
                      openCapacitorSite(departmentInfo.officialURL, "_blank");
                    }}
                  >
                    {t('common:visitOfficialWebsite', 'Visit Official Website')}
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <br /><br />

      {/* Senators Carousel - Desktop */}
      <div id='branchPage' style={{ marginBottom: '1rem' }}>
        <Container className='bgcolor'>
          <Container id='carouselBufferOne'>
            <Row>
              <Col md={12}>
                <MembersCarouselSection
                  memberType={MEMBER_TYPES.SENATE}
                  limit={10}
                  title={t('common:usSenators', 'U.S. Senators')}
                  showViewAll={true}
                  viewAllLink="/us/senators"
                />
              </Col>
            </Row>
          </Container>
        </Container>
      </div>

      {/* Committee Feeds - Using DeskCarousel */}
      {committeeRows.map((row, rowIndex) => (
        <div key={`committee-row-${rowIndex}`} id='branchPage' style={{ marginBottom: '1rem' }}>
          <Container className='bgcolor'>
            <Container id='carouselBufferOne'>
              <Row>
                {row.map((committee) => {
                  const transformedCommittee = transformCommitteeForCarousel(committee);
                  const committeeRoute = `/us/senate/${committee.id}`;
                  
                  return (
                    <Col key={committee.id} md={6}>
                      <DeskCarousel 
                        title={committee.shortName || committee.name}
                        sections={generateFeedVariations(transformedCommittee, 10)}
                        viewAllLink={committeeRoute}
                        intervalDuration={carouselInterval}
                        showAll={true}
                        showFeedTitle={false}
                        useViewAllLinkForTitle={true}
                      />
                    </Col>
                  );
                })}
              </Row>
            </Container>
          </Container>
        </div>
      ))}
    </div>
  );
};

export default SenatePage;